<template>
  <div
    :class="[ns.b(), ns.is('disabled', disabled), ns.m(_size)]"
    :style="{ 'max-width': width + 'px' }"
  >
    <el-input
      ref="inputRef"
      v-model="searchValue"
      clearable
      v-bind="userAttrsCopy"
      :disabled="disabled"
      :class="ns.e('input')"
      :style="{ width: width + 'px' }"
      @click="handleFocus"
    >
      <template #prepend>
        <template v-if="$slots.prepend">
          <div :class="ns.e('prepend')">
            <slot name="prepend" />
          </div>
        </template>
        <el-select
          ref="selectRef"
          v-model="optionValue"
          style="height: 100%"
          :class="[ns.e('select'), ns.is('disabled', disabled)]"
          :disabled="disabled"
          @change="changeOption"
        >
          <template #prefix>
            <span :class="ns.is('empty', options.length <= 0)">
              {{
                options.find((s) => s.value === optionValue)?.label ?? emptyText
              }}
            </span>
          </template>
          <el-option
            v-for="item in options"
            :key="item.value"
            :label="item[labelValue.label]"
            :value="item[labelValue.value]"
          />
        </el-select>
      </template>
      <template #suffix>
        <el-icon :class="ns.e('batch-icon')" @click.stop="handleSearch"
          ><Search
        /></el-icon>
        <el-popover
          effect="light"
          placement="bottom-end"
          :popper-class="ns.e('batch-popper')"
          :class="ns.e('batch-tooltip')"
          :visible="tooltipVisible"
          :offset="20"
          :width="Number(width) - selectWidth"
        >
          <el-input
            ref="textareaRef"
            v-model="inputValue"
            resize="none"
            :autosize="{ minRows: minRows, maxRows: 12 }"
            :class="ns.e('textarea')"
            :placeholder="'精确搜索，一行一项，最多支持' + rows + '行'"
            type="textarea"
            @keydown.enter="handleEnterKey"
            @input="handleTextareaInput"
          />
          <div :class="ns.e('controls')">
            <el-button size="small" plain @click.stop="handleClear"
              >清空</el-button
            >
            <div>
              <el-button size="small" plain @click.stop="handleClose"
                >关闭</el-button
              >
              <el-button
                size="small"
                :class="ns.e('search-button')"
                plain
                @click.stop="handleMultipleSearch"
                >搜索</el-button
              >
            </div>
          </div>
          <template #reference>
            <el-icon :class="ns.e('tooltip-icon')" @click.stop="showTooltip"
              ><Operation
            /></el-icon>
          </template>
        </el-popover>
      </template>
    </el-input>
    <div v-show="tagShow" :class="ns.e('tag')">
      <el-popover
        :width="Number(width) - selectWidth"
        :popper-class="ns.e('batch-popper')"
        effect="light"
        placement="bottom-start"
      >
        <div
          :class="ns.e('tag-content')"
          :style="{ 'min-width': Number(width) - selectWidth + 'px' }"
        >
          <el-scrollbar>
            <p v-for="item in multipleSearchValue" :key="item">{{ item }}</p>
          </el-scrollbar>
        </div>
        <template #reference>
          <el-tag closable @close="handleCloseTag"
            >{{ selectedLabel }}：{{
              multipleSearchValue.slice(0, 3).join(', ')
            }}
            等{{ multipleSearchValue.length }}项
          </el-tag>
        </template>
      </el-popover>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, reactive, ref, toRef, useAttrs } from 'vue'
import { Operation, Search } from '@element-plus/icons-vue'
import { ElMessage } from '@code-monkey-ui/components/message'
import ElInput from '@code-monkey-ui/components/input'
import { ElScrollbar } from '@code-monkey-ui/components/scrollbar'
import { ElOption, ElSelect } from '@code-monkey-ui/components/select'
import ElIcon from '@code-monkey-ui/components/icon'
import ElPopover from '@code-monkey-ui/components/popover'
import { ElButton } from '@code-monkey-ui/components/button'
import { ElTag } from '@code-monkey-ui/components/tag'
import { useNamespace } from '@code-monkey-ui/hooks'
import { batchEmits, batchProps } from './batch'
import { useBatch } from './use-batch'
const props = defineProps(batchProps)

const useSize = reactive({
  size: toRef(props, 'size'),
})
const ns = useNamespace('batch')
const emit = defineEmits(batchEmits)
const { disabled, _size } = useBatch(props, useSize.size)
const userAttrs = useAttrs()
const textareaRef = ref<any>()
const userAttrsCopy = reactive<any>({ ...userAttrs })
// 不被需要的props
delete userAttrsCopy.size
delete userAttrsCopy.prefixIcon
delete userAttrsCopy.suffixIcon
delete userAttrsCopy.rows
delete userAttrsCopy.readonly
delete userAttrsCopy.tabindex
delete userAttrsCopy.modelValue
delete userAttrsCopy.resize
delete userAttrsCopy.autosize
delete userAttrsCopy.type

defineOptions({
  name: 'ElBatch',
})

const inputRef = ref<any>(null)
const selectRef = ref<any>(null)
const tooltipVisible = ref<boolean>(false)
const optionValue = ref(props.options[0]?.value)
const selectWidth = ref<number>(0)
const searchValue = ref<string>('')
const inputValue = ref('')
const selectedLabel = ref<string>('')
const multipleSearchValue = ref<any[]>([])
const tagShow = ref<boolean>(false)

/**
 * 处理批量搜索框回车键事件
 * @param event 事件
 */
const handleEnterKey = (event: Event) => {
  const lines = inputValue.value.split('\n')
  const lastLine = lines[lines.length - 1]
  nextTick(() => {
    const textarea = textareaRef.value?.textarea
    textarea.selectionStart = textarea.selectionEnd = textarea.value.length
  })
  if (!lastLine.trim() || lines.length >= props.rows) {
    event.preventDefault()
  }
}

/**
 * 处理批量搜索框输入事件
 * @param value 输入的值
 */
const handleTextareaInput = (value: string) => {
  const inputElement = textareaRef.value.$el.querySelector('textarea')
  inputElement.addEventListener('paste', preventPaste)
  const lines = value.split('\n')
  const nonEmptyLines = lines.filter((line) => line.trim() !== '')
  if (nonEmptyLines.length >= props.rows) {
    inputValue.value = lines.slice(0, props.rows).join('\n')
  } else {
    inputElement.removeEventListener('paste', preventPaste)
  }
}

/**
 * 阻止粘贴事件
 * @param event 事件
 */
const preventPaste = (event: ClipboardEvent) => {
  event.preventDefault()
}

/**
 * 处理输入框聚焦事件
 */
const handleFocus = () => {
  handleClose()
  nextTick(() => {
    inputRef.value.blur()
    inputRef.value.focus()
  })
}

/**
 * 处理选择框改变事件
 * @param value 选择的值
 */
const changeOption = (value: any) => {
  handleClose()
  emit('optionsChange', value)
}

/**
 * 处理搜索事件
 */
const handleSearch = () => {
  if (disabled.value) return
  emit('search', {
    searchValue: searchValue.value,
    optionValue: optionValue.value,
  })
}

/**
 * 处理清空事件
 */
const handleClear = () => {
  handleBlur()
  inputValue.value = ''
  emit('clearMultipleValue')
}

/**
 * 处理关闭事件
 */
const handleClose = () => {
  tooltipVisible.value = false
}

/**
 * 处理输入框失去焦点事件
 */
const handleBlur = () => {
  const focusElement = inputRef.value.$el.querySelector('.is-focus')
  const activeElement = document.activeElement as HTMLInputElement
  activeElement?.blur()
  if (focusElement) {
    focusElement.classList.remove('is-focus')
  }
}

/**
 * 处理多选搜索事件
 */
const handleMultipleSearch = () => {
  handleBlur()
  handleClose()
  handleMultipleSearchData()
  nextTick(() => {
    selectedLabel.value = selectRef.value.selectedLabel
  })
  emit('multipleSearch', {
    multipleSearchValue: multipleSearchValue.value,
    optionsValue: optionValue.value,
  })
}

// 处理多选搜索数据
const handleMultipleSearchData = () => {
  const data = inputValue.value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
  multipleSearchValue.value = [...new Set(data)]
  if (data.length > multipleSearchValue.value.length) {
    ElMessage.warning('有重复数据,已自动去重')
  }
  inputValue.value = multipleSearchValue.value.join('\n')
  if (props.needTag) {
    tagShow.value = true
  }
  return multipleSearchValue.value
}

/**
 * 处理显示提示框事件
 */
const showTooltip = () => {
  if (disabled.value) return
  handleBlur()
  selectWidth.value = selectRef.value?.$el.offsetWidth
  tooltipVisible.value = !tooltipVisible.value
}

/**
 * 处理关闭标签事件
 */
const handleCloseTag = () => {
  tagShow.value = false
}

const clear = () => {
  multipleSearchValue.value = []
  optionValue.value = props.options[0]?.value
  searchValue.value = ''
}

onMounted(() => {
  changeOption(optionValue.value)
  if (disabled.value) return
  selectWidth.value = selectRef.value?.$el.offsetWidth
})

defineExpose({
  size: props.size,
  getFullData: () => {
    return {
      multipleSearchValue: handleMultipleSearchData(),
      optionsValue: optionValue.value,
      searchValue: searchValue.value,
    }
  },
  clear,
})
</script>
